import { useState, useEffect, useCallback } from 'react';
import { useLessonContext } from '../contexts/LessonContext';

export type HighlightColor = 'yellow' | 'green' | 'blue' | 'pink' | 'purple';

interface Highlight {
  id: string;
  type: 'sop' | 'bible';
  year: number;
  quarter: number;
  lessonNumber: number;
  dayNumber: number;
  questionIndex: number;
  verseIndex?: number;  // for Bible verses
  sopIndex?: number;    // for SOP quotes
  startOffset: number;
  endOffset: number;
  color: HighlightColor;
  language: string;  // Added language field
  groupId?: string;  // Optional: links highlights across multiple paragraphs
}

export const useHighlight = () => {
  const { year, quarter, lessonNumber } = useLessonContext();
  
  // Extract language from URL manually since there's no routing setup
  const getLangFromUrl = () => {
    const pathParts = window.location.pathname.split('/');
    // Expected format: /ssl/en/2025/4/4
    // pathParts: ['', 'ssl', 'en', '2025', '4', '4']
    return pathParts[2] || 'en';
  };
  
  const language = getLangFromUrl();
  const storageKey = `highlights-${language}-${year}-${quarter}-${lessonNumber}`;
  const isValidLesson = year !== 0 && quarter !== 0 && lessonNumber !== 0;

  const [highlights, setHighlights] = useState<Highlight[]>(() => {
    // Initial load will be empty since lesson info isn't valid yet
    return [];
  });

  // Load highlights when lesson info becomes valid
  useEffect(() => {
    if (!isValidLesson) return;
    
    const saved = localStorage.getItem(storageKey);
    if (!saved) return;
    
    try {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        setHighlights(parsed);
      }
    } catch (e) {
      console.error('Error parsing highlights from storage:', e);
    }
  }, [isValidLesson, storageKey]);

  // Save highlights to storage when they change
  useEffect(() => {
    // Only save to storage if we have valid lesson info and non-empty highlights
    if (!isValidLesson) return;
    
    // Get current storage value
    const currentStored = localStorage.getItem(storageKey);
    const currentHighlights = currentStored ? JSON.parse(currentStored) : [];
    
    // Only update storage if we have highlights and they're different from what's stored
    if (highlights.length > 0 && JSON.stringify(highlights) !== JSON.stringify(currentHighlights)) {
      localStorage.setItem(storageKey, JSON.stringify(highlights));
    }
  }, [highlights, storageKey, isValidLesson]);

  const mergeOverlappingHighlights = useCallback((
    existingHighlights: Highlight[],
    newStart: number,
    newEnd: number,
    newColor: HighlightColor
  ): { startOffset: number; endOffset: number; mergedIds: string[]; color: HighlightColor } => {
    let minOffset = newStart;
    let maxOffset = newEnd;
    const overlappingIds: string[] = [];
    let color = newColor;

    existingHighlights.forEach(highlight => {
      const overlaps = (
        (newStart <= highlight.endOffset && newEnd >= highlight.startOffset) ||
        (highlight.startOffset <= newEnd && highlight.endOffset >= newStart)
      );

      if (overlaps) {
        minOffset = Math.min(minOffset, highlight.startOffset);
        maxOffset = Math.max(maxOffset, highlight.endOffset);
        overlappingIds.push(highlight.id);
        color = highlight.color;
      }
    });

    return {
      startOffset: minOffset,
      endOffset: maxOffset,
      mergedIds: overlappingIds,
      color
    };
  }, []);

  const getHighlightsForSection = useCallback((
    type: 'sop' | 'bible',
    dayNumber: number, 
    questionIndex: number, 
    index?: number
  ) => {
    const filtered = highlights.filter(h => 
      h.type === type &&
      h.dayNumber === dayNumber && 
      h.questionIndex === questionIndex &&
      h.language === language &&
      (type === 'sop' ? h.sopIndex === index : h.verseIndex === index)
    );
    
    return filtered;
  }, [highlights, language]);

  const addHighlight = useCallback((
    type: 'sop' | 'bible',
    dayNumber: number, 
    questionIndex: number, 
    selection: Selection,
    sopIndex?: number,
    verseIndex?: number,
    color: HighlightColor = 'yellow'
  ) => {
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const container = range.startContainer.parentElement?.closest(type === 'sop' ? '.sop-text' : '.bible-verses');
    
    if (!container) return;

    // Get the text content before the selection
    const textBeforeStart = document.createRange();
    textBeforeStart.setStart(container, 0);
    textBeforeStart.setEnd(range.startContainer, range.startOffset);
    const startOffset = textBeforeStart.toString().length;

    // Get the text content before the end of selection
    const textBeforeEnd = document.createRange();
    textBeforeEnd.setStart(container, 0);
    textBeforeEnd.setEnd(range.endContainer, range.endOffset);
    const endOffset = textBeforeEnd.toString().length;

    const text = selection.toString();

    if (text.trim() === '') return;

    // console.log('Adding highlight:', {
    //   type,
    //   dayNumber,
    //   questionIndex,
    //   startOffset,
    //   endOffset,
    //   text
    // });

    setHighlights(prev => {
      const sectionHighlights = prev.filter(h => 
        h.type === type &&
        h.dayNumber === dayNumber &&
        h.questionIndex === questionIndex &&
        h.language === language &&
        (type === 'sop' ? h.sopIndex === sopIndex : h.verseIndex === verseIndex)
      );

      const { startOffset: mergedStart, endOffset: mergedEnd, mergedIds, color: finalColor } = mergeOverlappingHighlights(
        sectionHighlights,
        startOffset,
        endOffset,
        color
      );

      const remainingHighlights = prev.filter(h => !mergedIds.includes(h.id));

      const newHighlight: Highlight = {
        id: `highlight-${Date.now()}`,
        type,
        year,
        quarter,
        lessonNumber,
        dayNumber,
        questionIndex,
        ...(type === 'sop' ? { sopIndex } : { verseIndex }),
        startOffset: mergedStart,
        endOffset: mergedEnd,
        color: finalColor,
        language  // Add language to new highlights
      };

      return [...remainingHighlights, newHighlight];
    });
  }, [year, quarter, lessonNumber, language, mergeOverlappingHighlights]);

  const updateHighlightColor = useCallback((highlightId: string, color: HighlightColor) => {
    setHighlights(prev => {
      // Find the highlight and check if it's part of a group
      const targetHighlight = prev.find(h => h.id === highlightId);
      if (!targetHighlight) return prev;

      // If it has a groupId, update all highlights in the group
      if (targetHighlight.groupId) {
        return prev.map(h =>
          h.groupId === targetHighlight.groupId ? { ...h, color } : h
        );
      }

      // Otherwise, just update this specific highlight
      return prev.map(h =>
        h.id === highlightId ? { ...h, color } : h
      );
    });
  }, []);

  const removeHighlight = useCallback((highlightId: string) => {
    setHighlights(prev => {
      // Find the highlight and check if it's part of a group
      const targetHighlight = prev.find(h => h.id === highlightId);
      if (!targetHighlight) return prev;

      // If it has a groupId, remove all highlights in the group
      if (targetHighlight.groupId) {
        return prev.filter(h => h.groupId !== targetHighlight.groupId);
      }

      // Otherwise, just remove this specific highlight
      return prev.filter(h => h.id !== highlightId);
    });
  }, []);

  // Create a multi-paragraph highlight by creating multiple linked highlights
  const addMultiParagraphHighlight = useCallback((
    paragraphs: Array<{
      type: 'sop' | 'bible';
      dayNumber: number;
      questionIndex: number;
      sopIndex?: number;
      verseIndex?: number;
      startOffset: number;
      endOffset: number;
      text: string;
    }>,
    color: HighlightColor = 'yellow'
  ) => {
    if (paragraphs.length === 0) return;

    // Generate a unique group ID for all highlights in this selection
    const groupId = `group-${Date.now()}`;

    setHighlights(prev => {
      // Collect all merged IDs first
      const allMergedIds: string[] = [];

      const baseTimestamp = Date.now();
      const newHighlights: Highlight[] = paragraphs.map((para, idx) => {
        // Filter existing highlights for this paragraph
        const sectionHighlights = prev.filter(h =>
          h.type === para.type &&
          h.dayNumber === para.dayNumber &&
          h.questionIndex === para.questionIndex &&
          h.language === language &&
          (para.type === 'sop' ? h.sopIndex === para.sopIndex : h.verseIndex === para.verseIndex)
        );

        // Merge with any overlapping highlights in this paragraph
        const { startOffset: mergedStart, endOffset: mergedEnd, mergedIds, color: finalColor } =
          mergeOverlappingHighlights(sectionHighlights, para.startOffset, para.endOffset, color);

        // Collect merged IDs to remove later
        allMergedIds.push(...mergedIds);

        const newHighlight: Highlight = {
          id: `highlight-${baseTimestamp}-${idx}`,
          type: para.type,
          year,
          quarter,
          lessonNumber,
          dayNumber: para.dayNumber,
          questionIndex: para.questionIndex,
          ...(para.type === 'sop' ? { sopIndex: para.sopIndex } : { verseIndex: para.verseIndex }),
          startOffset: mergedStart,
          endOffset: mergedEnd,
          color: finalColor,
          language,
          groupId
        };

        return newHighlight;
      });

      // Remove all merged highlights at once, then add new ones
      const filteredPrev = prev.filter(h => !allMergedIds.includes(h.id));
      return [...filteredPrev, ...newHighlights];
    });
  }, [year, quarter, lessonNumber, language, mergeOverlappingHighlights]);

  return {
    addHighlight,
    addMultiParagraphHighlight,
    removeHighlight,
    updateHighlightColor,
    getHighlightsForSection
  };
}; 